import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography
  } from "@mui/material";
import { Cancel, Save } from "@mui/icons-material";
import { checkGroupNameExists } from "../../utils/utils";
import { Group, GroupWithId } from "../../interfaces/IGroups";
import { ParticipantsSelector } from "../shared/ParticipantsSelector";
import { ParticipantWithId } from "../../interfaces/IParticipants";
import { useGroupValidation } from "../../hooks/useGroupValidation";
import { useMemo, useState } from "react";

interface GroupFormProps {
  initialData?: GroupWithId;
  allParticipants: ParticipantWithId[];
  existingGroups: GroupWithId[];
  onSubmit: (
    groupData: Omit<Group, "userId" | "createdAt" | "updatedAt">,
  ) => Promise<{ success: boolean; error?: string }>;
  onCancel: () => void;
}

const MAX_PARTICIPANTS = 50;

export default function GroupForm({
  initialData,
  allParticipants,
  existingGroups,
  onSubmit,
  onCancel,
}: GroupFormProps) {
  const [name, setName] = useState(initialData?.name || "");
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>(
    initialData?.participantIds || [],
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { validateGroupName, validateParticipantCount } = useGroupValidation();

  // Walidacja nazwy - użyj useMemo jak w formularzu prezentacji
  const nameError = useMemo(() => {
    const nameValue = name || "";
    if (!nameValue.trim()) {
      return "Nazwa grupy jest wymagana";
    }
    // Sprawdź walidację długości i formatu
    const validationError = validateGroupName(nameValue);
    if (validationError) {
      return validationError;
    }
    // Sprawdź unikalność nazwy (jeśli edycja, wyklucz aktualną grupę)
    if (checkGroupNameExists(existingGroups, nameValue, initialData?.id)) {
      return "Grupa o takiej nazwie już istnieje.";
    }
    return null;
  }, [name, existingGroups, initialData?.id, validateGroupName]);

  // Walidacja liczby zawodników
  const participantCountError = validateParticipantCount(selectedParticipantIds);

  const selectedParticipants = allParticipants.filter((p) =>
    selectedParticipantIds.includes(p.id || ""),
  );

  const availableParticipants = allParticipants.filter(
    (p) => !selectedParticipantIds.includes(p.id || ""),
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setSubmitError(null);
  };

  const handleParticipantsChange = (participants: ParticipantWithId[]) => {
    setSelectedParticipantIds(participants.map((p) => p.id || "").filter(Boolean));
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    // Reset błędów
    setSubmitError(null);

    // Walidacja nazwy (nameError jest już obliczany w useMemo)
    if (nameError) {
      return;
    }

    // Walidacja liczby zawodników
    if (participantCountError) {
      setSubmitError(participantCountError);
      return;
    }

    setIsSubmitting(true);

    const result = await onSubmit({
      name: name.trim(),
      participantIds: selectedParticipantIds,
    });

    setIsSubmitting(false);

    if (!result.success) {
      setSubmitError(result.error || "Nie udało się zapisać grupy.");
    }
  };

  const isFormValid = !nameError && !participantCountError && name.trim().length > 0;

  return (
    <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
            {initialData ? "Edytuj grupę" : "Utwórz nową grupę"}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Cancel />}
              onClick={onCancel}
              disabled={isSubmitting}
              sx={{
                color: "#6b7280",
                borderColor: "#6b7280",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  borderColor: "#6b7280",
                },
                py: 1,
                px: 1.5,
                fontSize: "0.75rem",
              }}
            >
              Anuluj
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Save />}
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              sx={{
                backgroundColor: "#10b981",
                "&:hover": { backgroundColor: "#059669" },
                "&:disabled": { backgroundColor: "#9ca3af" },
                py: 1,
                px: 1.5,
                fontSize: "0.75rem",
              }}
            >
              {initialData ? "Zapisz zmiany" : "Utwórz grupę"}
            </Button>
          </Box>
        </Box>

        {/* Pole nazwy */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#2f2f2f" }}>
                  Nazwa grupy (wymagane)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={name || ""}
                  onChange={handleNameChange}
                  error={!!nameError}
                  helperText={nameError || "Nazwa grupy (max. 50 znaków)"}
                  placeholder="Wprowadź nazwę grupy"
                  disabled={isSubmitting}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Komunikat o liczbie zawodników - zawsze widoczny */}
        <Alert
          severity={selectedParticipantIds.length > MAX_PARTICIPANTS ? "error" : "info"}
          sx={{
            mb: 2,
            border: "1px solid",
            borderColor:
              selectedParticipantIds.length > MAX_PARTICIPANTS ? "error.main" : "info.main",
          }}
        >
          {selectedParticipantIds.length > MAX_PARTICIPANTS
            ? participantCountError
            : selectedParticipantIds.length === 0
            ? `Maksymalnie możesz przypisać ${MAX_PARTICIPANTS} zawodników do grupy.`
            : `Wybrano ${selectedParticipantIds.length} z maksymalnie ${MAX_PARTICIPANTS} zawodników.`}
        </Alert>

        {/* Błąd submitowania */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {/* Wybór zawodników */}
        <Box>
          <ParticipantsSelector<ParticipantWithId>
            allItems={availableParticipants}
            selectedItems={selectedParticipants}
            setSelectedItems={handleParticipantsChange}
            getLabel={(participant: ParticipantWithId) =>
              `${participant.firstName} ${participant.lastName} | ${new Date(
                participant.birthDate,
              ).toLocaleDateString()}`
            }
          />
        </Box>
      </CardContent>
    </Card>
  );
}

